import api from './api';

export interface TestResult {
  id: string;
  testId: string;
  testTitle: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  score: number;
  maxScore: number;
  percentage: number;
  totalQuestions: number;
  correctAnswers: number;
  wrongAnswers: number;
  unansweredQuestions: number;
  timeSpent: number; // in minutes
  submittedAt: string;
  status: 'completed' | 'in-progress' | 'not-started';
  className?: string;
  batchName?: string;
}

export interface TestStatistics {
  testId: string;
  testTitle: string;
  totalAssigned: number;
  totalCompleted: number;
  totalInProgress: number;
  averageScore: number;
  averageTime: number;
  highestScore: number;
  lowestScore: number;
  completionRate: number;
}

export interface TestResultsResponse {
  content: TestResult[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

class TestResultsApi {
  private baseUrl = '/admin/test-results';

  /**
   * Get all test results with pagination and filters
   */
  async getAllResults(
    page: number = 0,
    size: number = 20,
    searchTerm?: string,
    statusFilter?: string,
    testFilter?: string
  ): Promise<TestResultsResponse> {
    try {
      const params: any = { page, size };
      
      if (searchTerm) params.search = searchTerm;
      if (statusFilter && statusFilter !== 'ALL') params.status = statusFilter;
      if (testFilter && testFilter !== 'ALL') params.testId = testFilter;

      const response = await api.get(this.baseUrl, { params });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch test results:', error);
      throw error;
    }
  }

  /**
   * Get test statistics
   */
  async getTestStatistics(): Promise<TestStatistics[]> {
    try {
      const response = await api.get(`${this.baseUrl}/statistics`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch test statistics:', error);
      throw error;
    }
  }

  /**
   * Get results for a specific test
   */
  async getTestResults(testId: string, page: number = 0, size: number = 20): Promise<TestResultsResponse> {
    try {
      const response = await api.get(`${this.baseUrl}/test/${testId}`, {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch results for test ${testId}:`, error);
      throw error;
    }
  }

  /**
   * Get results for a specific student
   */
  async getStudentResults(studentId: string, page: number = 0, size: number = 20): Promise<TestResultsResponse> {
    try {
      const response = await api.get(`${this.baseUrl}/student/${studentId}`, {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch results for student ${studentId}:`, error);
      throw error;
    }
  }

  /**
   * Export test results as CSV
   */
  async exportResults(
    searchTerm?: string,
    statusFilter?: string,
    testFilter?: string
  ): Promise<Blob> {
    try {
      const params: any = {};
      
      if (searchTerm) params.search = searchTerm;
      if (statusFilter && statusFilter !== 'ALL') params.status = statusFilter;
      if (testFilter && testFilter !== 'ALL') params.testId = testFilter;

      const response = await api.get(`${this.baseUrl}/export`, {
        params,
        responseType: 'blob'
      });
      
      return response.data;
    } catch (error) {
      console.error('Failed to export test results:', error);
      throw error;
    }
  }

  /**
   * Get analytics summary
   */
  async getAnalyticsSummary(): Promise<{
    totalResults: number;
    totalTests: number;
    averageScore: number;
    completionRate: number;
    topPerformers: TestResult[];
    recentResults: TestResult[];
  }> {
    try {
      const response = await api.get(`${this.baseUrl}/analytics/summary`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch analytics summary:', error);
      throw error;
    }
  }

  /**
   * Get performance trends
   */
  async getPerformanceTrends(testId?: string, period: string = '30d'): Promise<{
    labels: string[];
    averageScores: number[];
    completionRates: number[];
  }> {
    try {
      const params: any = { period };
      if (testId) params.testId = testId;

      const response = await api.get(`${this.baseUrl}/analytics/trends`, { params });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch performance trends:', error);
      throw error;
    }
  }

  /**
   * Get class-wise performance
   */
  async getClassPerformance(): Promise<{
    className: string;
    totalStudents: number;
    averageScore: number;
    completionRate: number;
  }[]> {
    try {
      const response = await api.get(`${this.baseUrl}/analytics/class-performance`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch class performance:', error);
      throw error;
    }
  }

  /**
   * Get subject-wise performance
   */
  async getSubjectPerformance(): Promise<{
    subject: string;
    totalTests: number;
    averageScore: number;
    completionRate: number;
  }[]> {
    try {
      const response = await api.get(`${this.baseUrl}/analytics/subject-performance`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch subject performance:', error);
      throw error;
    }
  }
}

export const testResultsApi = new TestResultsApi();
export default testResultsApi;
